import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, useEdgesState, useNodesState,
  MarkerType, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  ArrowLeft, Save, Play, Trash2, Zap, Filter, Clock, Mail,
  CheckCircle2, Plus, Tag, StickyNote, Flame, GitBranch, ArrowRightCircle,
  PlusCircle, Calendar, Clipboard, MessageSquare, X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// ---- Icon map (matches the icon key returned by /automations/catalog) ------
const ICONS = {
  "arrow-right-circle": ArrowRightCircle, "plus-circle": PlusCircle, "flame": Flame,
  "calendar": Calendar, "clipboard": Clipboard, "git-branch": GitBranch,
  "clock": Clock, "mail": Mail, "check-circle": CheckCircle2, "plus": Plus,
  "tag": Tag, "sticky-note": StickyNote,
};

// ---- Custom node renderer ---------------------------------------------------
// Every node in the canvas uses this component. Colour codes:
//   trigger.* → amber, filter → sky, delay → violet, action.* → emerald.
function CanvasNode({ id, data, selected }) {
  const kind = data.__kind || "";
  const label = data.label || data.__defaultLabel || kind;
  const Icon = ICONS[data.__icon] || Zap;

  const family = kind.startsWith("trigger")
    ? "amber"
    : kind === "filter"
      ? "sky"
      : kind === "delay"
        ? "violet"
        : "emerald";
  const colors = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    sky: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    violet: "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  };

  return (
    <div
      className={
        "relative rounded-xl border-2 bg-card px-4 py-3 min-w-[190px] shadow-sm transition " +
        (selected ? "ring-2 ring-primary ring-offset-2 ring-offset-background " : "") +
        colors[family]
      }
      data-testid={`canvas-node-${id}`}
    >
      {!kind.startsWith("trigger") && (
        <Handle type="target" position={Position.Left} className="!bg-primary !w-2.5 !h-2.5" />
      )}
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">
            {family === "amber" ? "When" : family === "sky" ? "If" : family === "violet" ? "Wait" : "Do"}
          </div>
          <div className="text-sm font-medium truncate">{label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-primary !w-2.5 !h-2.5" />
    </div>
  );
}

const nodeTypes = { canvasNode: CanvasNode };

// ---- Palette group renderer ------------------------------------------------
function PaletteGroup({ title, items, onDragStart }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">{title}</div>
      <div className="space-y-1.5">
        {items.map((item) => {
          const Icon = ICONS[item.icon] || Zap;
          return (
            <div
              key={item.type}
              draggable
              onDragStart={(e) => onDragStart(e, item)}
              className="cursor-grab active:cursor-grabbing rounded-md border border-border bg-background hover:border-primary/40 hover:bg-secondary/40 px-2.5 py-2 text-xs flex items-center gap-2 transition"
              data-testid={`palette-${item.type}`}
              title={`Drag onto canvas`}
            >
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Inspector for a selected node -----------------------------------------
function Inspector({ node, catalog, onChange, onRemove, onClose }) {
  if (!node) {
    return (
      <div className="text-xs text-muted-foreground p-6 text-center">
        Click a node to configure it. Drag from the left palette to add new nodes.
      </div>
    );
  }
  const kind = node.data.__kind;
  const schema = (() => {
    if (kind.startsWith("trigger")) return (catalog?.triggers || []).find((x) => x.type === kind);
    if (kind === "filter") return (catalog?.filters || [])[0];
    if (kind === "delay") return (catalog?.delays || [])[0];
    return (catalog?.actions || []).find((x) => x.type === kind);
  })();
  const fields = schema?.config_schema || [];

  const updField = (key, val) => {
    onChange({ ...node, data: { ...node.data, [key]: val } });
  };

  return (
    <div className="p-4 space-y-4" data-testid="inspector-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {kind.startsWith("trigger") ? "Trigger" : kind === "filter" ? "Filter" : kind === "delay" ? "Delay" : "Action"}
          </div>
          <div className="text-sm font-medium">{schema?.label || kind}</div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-foreground/10" data-testid="inspector-close"><X className="h-4 w-4" /></button>
      </div>

      <div>
        <label className="text-xs">Label (shown on canvas)</label>
        <input
          value={node.data.label || ""}
          onChange={(e) => updField("label", e.target.value)}
          className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
          data-testid="inspector-label"
        />
      </div>

      {fields.length === 0 && (
        <div className="text-xs text-muted-foreground italic">
          No configuration needed — this node fires automatically.
        </div>
      )}

      {fields.map((f) => (
        <div key={f.key}>
          <label className="text-xs">{f.label}</label>
          {f.hint && <div className="text-[10px] text-muted-foreground mt-0.5">{f.hint}</div>}
          {f.kind === "select" ? (
            <select
              value={node.data[f.key] ?? ""}
              onChange={(e) => updField(f.key, e.target.value)}
              className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              data-testid={`inspector-field-${f.key}`}
            >
              <option value="" disabled>Choose…</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : f.kind === "textarea" ? (
            <textarea
              value={node.data[f.key] ?? ""}
              onChange={(e) => updField(f.key, e.target.value)}
              rows={4}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              placeholder="Use {{lead.business_name}} etc."
              data-testid={`inspector-field-${f.key}`}
            />
          ) : f.kind === "number" ? (
            <input
              type="number"
              value={node.data[f.key] ?? ""}
              onChange={(e) => updField(f.key, e.target.value === "" ? "" : Number(e.target.value))}
              className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              data-testid={`inspector-field-${f.key}`}
            />
          ) : f.kind === "bool" ? (
            <div className="mt-1 flex items-center gap-2">
              <Switch
                checked={!!node.data[f.key]}
                onCheckedChange={(v) => updField(f.key, v)}
                data-testid={`inspector-field-${f.key}`}
              />
              <span className="text-xs text-muted-foreground">{node.data[f.key] ? "On" : "Off"}</span>
            </div>
          ) : (
            <input
              value={node.data[f.key] ?? ""}
              onChange={(e) => updField(f.key, e.target.value)}
              className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
              data-testid={`inspector-field-${f.key}`}
            />
          )}
        </div>
      ))}

      {!kind.startsWith("trigger") && (
        <div className="pt-2 border-t border-border">
          <button
            onClick={() => onRemove(node.id)}
            className="text-xs text-destructive inline-flex items-center gap-1.5 hover:opacity-80"
            data-testid="inspector-remove-node"
          >
            <Trash2 className="h-3 w-3" /> Remove this node
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Main page --------------------------------------------------------------
export default function AutomationBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [auto, setAuto] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [mobileTab, setMobileTab] = useState("canvas");  // lg+ ignores this
  const wrapper = useRef(null);
  const [rfInst, setRfInst] = useState(null);

  const iconFor = useCallback((type) => {
    const flat = [
      ...(catalog?.triggers || []),
      ...(catalog?.filters || []),
      ...(catalog?.delays || []),
      ...(catalog?.actions || []),
    ];
    return flat.find((n) => n.type === type)?.icon || "zap";
  }, [catalog]);

  const labelFor = useCallback((type) => {
    const flat = [
      ...(catalog?.triggers || []),
      ...(catalog?.filters || []),
      ...(catalog?.delays || []),
      ...(catalog?.actions || []),
    ];
    return flat.find((n) => n.type === type)?.label || type;
  }, [catalog]);

  // ---- Load automation + catalog ----------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [aRes, cRes] = await Promise.all([
          api.get(`/automations/${id}`),
          api.get("/automations/catalog"),
        ]);
        setCatalog(cRes.data.nodes);
        setAuto(aRes.data);
        // React Flow expects `type: 'canvasNode'` + we store the original
        // node kind on data.__kind so the renderer can style + Inspector
        // knows the schema.
        const rfNodes = (aRes.data.canvas?.nodes || []).map((n) => ({
          id: n.id,
          type: "canvasNode",
          position: n.position || { x: 100, y: 100 },
          data: {
            ...(n.data || {}),
            __kind: n.type,
            __icon: (cRes.data.nodes.triggers.find((x) => x.type === n.type)
              || cRes.data.nodes.filters.find((x) => x.type === n.type)
              || cRes.data.nodes.delays.find((x) => x.type === n.type)
              || cRes.data.nodes.actions.find((x) => x.type === n.type) || {}).icon,
          },
        }));
        setNodes(rfNodes);
        setEdges((aRes.data.canvas?.edges || []).map((e, i) => ({
          id: e.id || `e-${i}-${e.source}-${e.target}`,
          source: e.source, target: e.target,
          animated: true, markerEnd: { type: MarkerType.ArrowClosed },
        })));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load automation");
        nav("/app/automation");
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // ---- Save --------------------------------------------------------------
  const onSave = async () => {
    if (!auto) return;
    setSaving(true);
    try {
      // Map React Flow nodes back to the storage shape.
      const outNodes = nodes.map((n) => ({
        id: n.id,
        type: n.data.__kind,
        position: n.position,
        data: Object.fromEntries(Object.entries(n.data).filter(([k]) => !k.startsWith("__"))),
      }));
      const outEdges = edges.map((e) => ({ id: e.id, source: e.source, target: e.target }));

      const { data } = await api.patch(`/automations/${id}`, {
        name: auto.name,
        description: auto.description,
        canvas: { nodes: outNodes, edges: outEdges },
      });
      setAuto(data);
      toast.success("Automation saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // ---- Toggle enabled ----------------------------------------------------
  const toggleEnabled = async () => {
    try {
      const { data } = await api.post(`/automations/${id}/toggle`);
      setAuto((a) => ({ ...a, enabled: data.enabled }));
      toast.success(data.enabled ? "Automation is live." : "Automation paused.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  // ---- Test run ----------------------------------------------------------
  const testRun = async () => {
    setTesting(true);
    try {
      // Save first so the test uses the current canvas.
      await onSave();
      const { data } = await api.post(`/automations/${id}/test`, {});
      setLastRun(data);
      toast.success(`Test run ${data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test run failed");
    } finally { setTesting(false); }
  };

  const loadRuns = async () => {
    try {
      const { data } = await api.get(`/automations/${id}/runs?limit=20`);
      setRuns(data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (runsOpen) loadRuns(); /* eslint-disable-next-line */ }, [runsOpen]);

  // ---- Drag from palette → canvas drop -----------------------------------
  const onDragStart = (e, item) => {
    e.dataTransfer.setData("application/reactflow", JSON.stringify(item));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = useCallback((e) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
  }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/reactflow");
    if (!raw || !rfInst) return;
    const item = JSON.parse(raw);
    // Reject if trying to add a second trigger — the engine only walks from one.
    if (item.type.startsWith("trigger.") && nodes.some((n) => (n.data.__kind || "").startsWith("trigger."))) {
      toast.error("This automation already has a trigger. Delete the current one first.");
      return;
    }
    const pos = rfInst.project({ x: e.clientX - (wrapper.current?.getBoundingClientRect().left || 0),
                                 y: e.clientY - (wrapper.current?.getBoundingClientRect().top || 0) });
    const newNode = {
      id: `${item.type.replace(/\./g, "-")}-${Date.now().toString(36)}`,
      type: "canvasNode",
      position: pos,
      data: { __kind: item.type, __icon: item.icon, __defaultLabel: item.label, label: item.label },
    };
    setNodes((ns) => ns.concat(newNode));
  }, [rfInst, nodes, setNodes]);

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, animated: true, markerEnd: { type: MarkerType.ArrowClosed } }, eds));
  }, [setEdges]);

  const onNodeClick = (_, node) => setSelectedNodeId(node.id);
  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

  const updateNode = (updated) => {
    setNodes((ns) => ns.map((n) => (n.id === updated.id ? { ...updated } : n)));
  };
  const removeNode = (nodeId) => {
    setNodes((ns) => ns.filter((n) => n.id !== nodeId));
    setEdges((es) => es.filter((e) => e.source !== nodeId && e.target !== nodeId));
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
  };

  if (!auto || !catalog) {
    return <div className="p-8 text-sm text-muted-foreground">Loading canvas…</div>;
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-background" data-testid="automation-builder">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3 flex items-center gap-3 flex-wrap">
        <Link to="/app/automation" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground" data-testid="auto-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Automations
        </Link>
        <div className="h-5 w-px bg-border" />
        <input
          value={auto.name}
          onChange={(e) => setAuto((a) => ({ ...a, name: e.target.value }))}
          className="text-lg font-medium bg-transparent focus:outline-none min-w-[180px] flex-1 sm:flex-none"
          data-testid="auto-name"
        />
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={!!auto.enabled} onCheckedChange={toggleEnabled} data-testid="auto-toggle-header" />
            <span className={"text-xs " + (auto.enabled ? "text-primary font-medium" : "text-muted-foreground")}>
              {auto.enabled ? "LIVE" : "PAUSED"}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={testRun} disabled={testing} className="rounded-full gap-1.5" data-testid="auto-test">
            <Play className="h-3.5 w-3.5" /> {testing ? "Running…" : "Test"}
          </Button>
          <Button size="sm" onClick={onSave} disabled={saving} className="rounded-full gap-1.5" data-testid="auto-save">
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Mobile drawer tabs (< lg only). At lg+ we show the full 3-column
          layout. Below lg we render one panel at a time so a phone has a
          usable canvas. */}
      <div className="lg:hidden flex border-b border-border bg-card">
        {[
          { key: "palette", label: "Palette" },
          { key: "canvas", label: "Canvas" },
          { key: "config", label: "Config", panel: "inspector" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setMobileTab(t.panel || t.key)}
            data-testid={`mobile-tab-${t.key}`}
            className={
              "flex-1 py-2 text-xs uppercase tracking-[0.2em] transition " +
              (mobileTab === (t.panel || t.key) ? "text-primary border-b-2 border-primary" : "text-muted-foreground border-b-2 border-transparent")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body: 3-column at lg+, single-panel tabbed on smaller screens. */}
      <div className="flex-1 lg:grid lg:grid-cols-[220px_1fr_320px] min-h-0 relative">
        {/* Palette */}
        <aside
          className={
            "border-r border-border bg-card p-3 overflow-y-auto " +
            "lg:block " + (mobileTab === "palette" ? "block" : "hidden")
          }
          data-testid="auto-palette"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Drag onto canvas
          </div>
          <PaletteGroup title="Triggers" items={catalog.triggers} onDragStart={onDragStart} />
          <PaletteGroup title="Filters" items={catalog.filters} onDragStart={onDragStart} />
          <PaletteGroup title="Delays" items={catalog.delays} onDragStart={onDragStart} />
          <PaletteGroup title="Actions" items={catalog.actions} onDragStart={onDragStart} />
        </aside>

        {/* Canvas */}
        <div
          ref={wrapper}
          className={
            "relative bg-background " +
            "lg:block " + (mobileTab === "canvas" ? "block h-[calc(100vh-160px)]" : "hidden")
          }
          onDragOver={onDragOver}
          onDrop={onDrop}
          data-testid="auto-canvas"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setRfInst}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onPaneClick={() => setSelectedNodeId(null)}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            defaultEdgeOptions={{ animated: true, markerEnd: { type: MarkerType.ArrowClosed } }}
          >
            <Background gap={16} />
            <Controls />
            <MiniMap pannable zoomable className="!bg-card !hidden lg:!block" />
          </ReactFlow>
        </div>

        {/* Inspector + last-run panel */}
        <aside
          className={
            "border-l border-border bg-card overflow-y-auto flex flex-col " +
            "lg:flex " + (mobileTab === "inspector" ? "flex" : "hidden")
          }
          data-testid="auto-inspector"
        >
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Inspector
              node={selectedNode}
              catalog={catalog}
              onChange={updateNode}
              onRemove={removeNode}
              onClose={() => setSelectedNodeId(null)}
            />
          </div>

          {/* Run history drawer */}
          <div className="border-t border-border">
            <button
              onClick={() => setRunsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
              data-testid="auto-history-toggle"
            >
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> Recent runs</span>
              <span>{runsOpen ? "Hide" : "Show"}</span>
            </button>
            {runsOpen && (
              <div className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto" data-testid="auto-history-list">
                {runs.length === 0 && <div className="text-[11px] text-muted-foreground">No runs yet. Click Test to fire one manually.</div>}
                {runs.map((r) => (
                  <div key={r.id} className="rounded-md border border-border bg-background/50 p-2 text-xs" data-testid={`run-row-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                      <span className={
                        "text-[10px] uppercase tracking-[0.18em] " +
                        (r.status === "completed" ? "text-emerald-600" :
                         r.status === "waiting" ? "text-violet-600" :
                         r.status === "stopped_by_filter" ? "text-sky-600" : "text-destructive")
                      }>{r.status}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      event: {r.event} · {(r.node_traces || []).length} step{(r.node_traces || []).length === 1 ? "" : "s"}
                    </div>
                    {(r.node_traces || []).slice(0, 4).map((t, i) => (
                      <div key={i} className="text-[10px] mt-0.5 flex gap-1.5">
                        <span className="opacity-60">·</span>
                        <span className="opacity-70">{t.kind}</span>
                        <span className={t.status === "ok" ? "text-emerald-600" : t.status === "waiting" ? "text-violet-600" : t.status === "stopped" ? "text-sky-600" : "text-destructive"}>
                          {t.status}
                        </span>
                        {t.message && <span className="text-muted-foreground truncate">{t.message}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Last test run modal — shadcn Dialog (Escape closes, focus-trapped). */}
      <Dialog open={!!lastRun} onOpenChange={(v) => { if (!v) setLastRun(null); }}>
        <DialogContent className="max-w-lg" data-testid="test-result-modal">
          <DialogHeader>
            <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
              Test run result
            </DialogTitle>
          </DialogHeader>
          {lastRun && (
            <>
              <div className={
                "inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full w-fit " +
                (lastRun.status === "completed" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" :
                 lastRun.status === "waiting" ? "bg-violet-500/15 text-violet-600 dark:text-violet-300" :
                 lastRun.status === "stopped_by_filter" ? "bg-sky-500/15 text-sky-600 dark:text-sky-300" :
                 "bg-destructive/15 text-destructive")
              }>
                <CheckCircle2 className="h-3.5 w-3.5" /> {lastRun.status}
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(lastRun.node_traces || []).map((t, i) => (
                  <div key={i} className="border-l-2 border-primary/40 pl-3 text-xs" data-testid={`test-trace-${i}`}>
                    <div className="font-medium">{t.kind}</div>
                    <div className={
                      t.status === "ok" ? "text-emerald-600 dark:text-emerald-300" :
                      t.status === "waiting" ? "text-violet-600 dark:text-violet-300" :
                      t.status === "stopped" ? "text-sky-600 dark:text-sky-300" :
                      "text-destructive"
                    }>
                      {t.status} — {t.message || ""}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
